import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Download,
  RefreshCw,
  Search,
  Users,
  Mail,
  Phone,
  Tag,
  Calendar,
  Smartphone,
  Monitor,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translate legacy source codes to Portuguese labels
const ORIGEM_LABELS = {
  hero: "Hero",
  sticky: "Fixo",
  availability: "Seção Agenda",
  pricing: "Seção Oferta de Hoje",
  summer: "Seção Sem Promoção",
  promo: "Seção Promoção",
  landing: "Landing",
};

function labelOrigem(o) {
  if (!o) return "Landing";
  if (o.startsWith("quiz:")) return "Quiz";
  return ORIGEM_LABELS[o] || o; // if already PT (e.g. "Seção Agenda"), returns as-is
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  const header = ["Nome", "E-mail", "WhatsApp", "Origem", "Dispositivo", "Data"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [header.join(",")].concat(
    rows.map((r) =>
      [r.nome, r.email, r.telefone, labelOrigem(r.origem), r.dispositivo || "-", fmtDate(r.created_at)]
        .map(escape)
        .join(",")
    )
  );
  return "\uFEFF" + lines.join("\n"); // BOM for Excel UTF-8
}

// Date range presets
const RANGES = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "7d", label: "Últimos 7 dias" },
  { key: "30d", label: "Últimos 30 dias" },
  { key: "tudo", label: "Tudo" },
];

function inRange(iso, key) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  switch (key) {
    case "hoje":
      return t >= startOfToday;
    case "ontem":
      return t >= startOfYesterday && t < startOfToday;
    case "7d":
      return t >= startOfToday - 6 * 24 * 60 * 60 * 1000;
    case "30d":
      return t >= startOfToday - 29 * 24 * 60 * 60 * 1000;
    default:
      return true;
  }
}

export default function DadosDM() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("tudo");
  const [device, setDevice] = useState("todos"); // todos | Mobile | Desktop

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Dados — Dive Master";
    fetchLeads();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (!inRange(l.created_at, range)) return false;
      if (device !== "todos" && (l.dispositivo || "-") !== device) return false;
      if (!term) return true;
      const origemLabel = labelOrigem(l.origem);
      return [l.nome, l.email, l.telefone, origemLabel, l.dispositivo].some((f) =>
        String(f || "").toLowerCase().includes(term)
      );
    });
  }, [leads, q, range, device]);

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `dive-master-leads-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    let hoje = 0;
    let ontem = 0;
    let semana = 0;
    for (const l of leads) {
      if (inRange(l.created_at, "hoje")) hoje += 1;
      if (inRange(l.created_at, "ontem")) ontem += 1;
      if (inRange(l.created_at, "7d")) semana += 1;
    }
    return { total: leads.length, hoje, ontem, semana };
  }, [leads]);

  // Breakdown by origem within the current filter
  const breakdown = useMemo(() => {
    const map = new Map();
    for (const l of filtered) {
      const k = labelOrigem(l.origem);
      map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-leads-page">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight text-brand-navy sm:text-3xl">
              Dive Master — Leads
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Todos os contatos capturados pelo formulário do site.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchLeads}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4" /> Atualizar
            </button>
            <button
              onClick={exportCSV}
              disabled={!filtered.length}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-4 py-2 text-sm font-bold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
              data-testid="admin-export"
            >
              <Download className="h-4 w-4" /> Exportar CSV
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Total" value={stats.total} icon={Users} />
          <StatCard label="Hoje" value={stats.hoje} icon={Tag} />
          <StatCard label="Ontem" value={stats.ontem} icon={Calendar} />
          <StatCard label="Últimos 7 dias" value={stats.semana} icon={Calendar} />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Date range */}
          <div className="flex flex-wrap gap-1.5 rounded-xl bg-white p-1 shadow-sm ring-1 ring-slate-200" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                  range === r.key
                    ? "bg-brand-navy text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`admin-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {/* Device */}
          <div className="flex gap-1.5 rounded-xl bg-white p-1 shadow-sm ring-1 ring-slate-200">
            {["todos", "Mobile", "Desktop"].map((d) => (
              <button
                key={d}
                onClick={() => setDevice(d)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                  device === d ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`admin-device-${d.toLowerCase()}`}
              >
                {d === "Mobile" && <Smartphone className="h-3.5 w-3.5" />}
                {d === "Desktop" && <Monitor className="h-3.5 w-3.5" />}
                {d === "todos" ? "Todos" : d}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, e-mail, WhatsApp, origem ou dispositivo…"
            className="flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-slate-400"
            data-testid="admin-search"
          />
          <span className="text-xs font-semibold text-slate-500">
            {filtered.length} de {leads.length}
          </span>
        </div>

        {/* Breakdown by origem */}
        {breakdown.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-breakdown">
            {breakdown.map(([k, v]) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200"
              >
                <span className="text-slate-500">{k}</span>
                <span className="rounded-full bg-brand-navy px-2 py-0.5 text-[10px] font-black text-white">
                  {v}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Carregando…</div>
          ) : error ? (
            <div className="p-10 text-center text-sm text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Nenhum lead encontrado para os filtros selecionados.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="hidden w-full text-left text-sm md:table" data-testid="admin-table">
                <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Nome</th>
                    <th className="px-5 py-3">E-mail</th>
                    <th className="px-5 py-3">WhatsApp</th>
                    <th className="px-5 py-3">Origem</th>
                    <th className="px-5 py-3">Dispositivo</th>
                    <th className="px-5 py-3">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id || `${l.email}-${l.created_at}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                      <td className="px-5 py-3 font-semibold text-brand-navy">{l.nome}</td>
                      <td className="px-5 py-3">
                        <a href={`mailto:${l.email}`} className="text-ocean-mid hover:underline">
                          {l.email}
                        </a>
                      </td>
                      <td className="px-5 py-3">
                        <a
                          href={`https://wa.me/${String(l.telefone || "").replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#25D366] hover:underline"
                        >
                          {l.telefone}
                        </a>
                      </td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          {labelOrigem(l.origem)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <DeviceBadge value={l.dispositivo} />
                      </td>
                      <td className="px-5 py-3 text-slate-500">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <ul className="divide-y divide-slate-100 md:hidden">
                {filtered.map((l) => (
                  <li key={l.id || `${l.email}-${l.created_at}`} className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-brand-navy">{l.nome}</p>
                      <div className="flex items-center gap-1.5">
                        <DeviceBadge value={l.dispositivo} compact />
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                          {labelOrigem(l.origem)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1 text-sm">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-ocean-mid">
                        <Mail className="h-4 w-4" /> {l.email}
                      </a>
                      <a
                        href={`https://wa.me/${String(l.telefone || "").replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-[#25D366]"
                      >
                        <Phone className="h-4 w-4" /> {l.telefone}
                      </a>
                      <p className="flex items-center gap-2 text-xs text-slate-500">
                        <Calendar className="h-3.5 w-3.5" /> {fmtDate(l.created_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="font-display text-2xl font-black text-brand-navy">{value}</p>
      </div>
    </div>
  );
}

function DeviceBadge({ value, compact = false }) {
  const isMobile = value === "Mobile";
  const isDesktop = value === "Desktop";
  const Icon = isMobile ? Smartphone : isDesktop ? Monitor : null;
  const label = value || "—";
  const color = isMobile
    ? "bg-emerald-100 text-emerald-700"
    : isDesktop
    ? "bg-indigo-100 text-indigo-700"
    : "bg-slate-100 text-slate-500";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${color}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {compact ? (isMobile ? "M" : isDesktop ? "D" : "—") : label}
    </span>
  );
}
